/**
 * DokumenteTab — Dokumente-Reiter im Fahrzeug-Detail-Drawer
 *
 * Zeigt Dokumente eines Fahrzeugs mit OCR-Status-Badge und
 * „Übernehmen"-Button für erkannte Felder (Issue #794).
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import {
  FileText, Loader2, AlertCircle, CheckCircle2, Clock,
  Scan, Check, ChevronDown, ChevronRight,
} from 'lucide-react';
import {
  isOcrRelevantKategorie,
  getOcrStatus,
  getFieldLabel,
  getOcrFieldValue,
  getOcrFieldKonfidenz,
  getFahrzeugFeldFuerOcr,
  isFieldAdopted,
  NUMERIC_FAHRZEUG_FELDER,
  parseEuropeanNumber,
  type OcrRun,
} from '@/lib/ocr-ui';

// ─── Typen ────────────────────────────────────────────────────────────────────

interface Dokument {
  id: string;
  dateiname?: string | null;
  name?: string | null;
  kategorie?: string | null;
  dateipfad?: string | null;
  ocr_extracted_fields?: unknown | null;
  created_at: string;
}

interface DokumenteTabProps {
  fahrzeugId: string;
  fahrzeug: Record<string, unknown>;
  onFahrzeugUpdated: () => void;
}

// ─── Hilfsfunktionen ──────────────────────────────────────────────────────────

const KATEGORIE_LABELS: Record<string, string> = {
  zulassung_zb1:    'Zulassung ZB1',
  zulassung_zb2:    'Zulassung ZB2',
  leasingvertrag:   'Leasingvertrag',
  versicherungspolice: 'Versicherungspolice',
  rechnung:         'Rechnung',
  foto:             'Foto',
  sonstiges:        'Sonstiges',
};

function formatDate(iso?: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  });
}

// ─── OCR-Status-Badge ─────────────────────────────────────────────────────────

function OcrStatusBadge({ status }: { status: 'ausstehend' | 'abgeschlossen' | 'fehler' | 'keine_felder' }) {
  if (status === 'abgeschlossen') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-50 text-green-700">
        <CheckCircle2 className="w-3 h-3" />
        OCR abgeschlossen
      </span>
    );
  }
  if (status === 'fehler') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-50 text-red-600">
        <AlertCircle className="w-3 h-3" />
        OCR Fehler
      </span>
    );
  }
  if (status === 'keine_felder') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-yellow-50 text-yellow-700">
        <AlertCircle className="w-3 h-3" />
        OCR: Keine Felder erkannt
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-100 text-gray-500">
      <Clock className="w-3 h-3" />
      OCR ausstehend
    </span>
  );
}

// ─── Konfidenz-Indikator ──────────────────────────────────────────────────────

function KonfidenzIndikator({ konfidenz }: { konfidenz: number | null }) {
  if (konfidenz === null) return null;
  const pct = Math.round(konfidenz * 100);
  const color = pct >= 90 ? 'bg-green-500' : pct >= 70 ? 'bg-yellow-400' : 'bg-red-400';
  return (
    <span
      className={`inline-flex items-center gap-1 text-[9px] font-bold px-1.5 py-0.5 rounded ${color} text-white`}
      title={`Konfidenz: ${pct}%`}
      aria-label={`Konfidenz: ${pct}%`}
    >
      {pct}%
    </span>
  );
}

// ─── Einzel-Dokument-Karte ────────────────────────────────────────────────────

function DokumentKarte({
  dok,
  ocrRuns,
  fahrzeug,
  onOcrTrigger,
  onUebernehmen,
}: {
  dok: Dokument;
  ocrRuns: OcrRun[];
  fahrzeug: Record<string, unknown>;
  onOcrTrigger: (dokumentId: string) => Promise<void>;
  onUebernehmen: (dokumentId: string, fahrzeugFeld: string, wert: string) => Promise<void>;
}) {
  const [expanded, setExpanded] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [adopting, setAdopting] = useState<string | null>(null);

  const isOcrRelevant = isOcrRelevantKategorie(dok.kategorie ?? '');
  const ocrStatus = isOcrRelevant ? getOcrStatus(dok, ocrRuns) : null;
  const dokName = dok.dateiname ?? dok.name ?? 'Dokument';
  const kategorieLabel = KATEGORIE_LABELS[dok.kategorie ?? ''] ?? dok.kategorie ?? '—';

  const ocrFelder = dok.ocr_extracted_fields && typeof dok.ocr_extracted_fields === 'object'
    ? Object.keys(dok.ocr_extracted_fields as Record<string, unknown>)
    : [];

  async function handleOcrTrigger() {
    setTriggering(true);
    try {
      await onOcrTrigger(dok.id);
    } finally {
      setTriggering(false);
    }
  }

  async function handleUebernehmen(ocrFeld: string, wert: string) {
    const fahrzeugFeld = getFahrzeugFeldFuerOcr(ocrFeld);
    if (!fahrzeugFeld) return;
    setAdopting(ocrFeld);
    try {
      await onUebernehmen(dok.id, fahrzeugFeld, wert);
    } finally {
      setAdopting(null);
    }
  }

  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
      {/* Dokument-Header */}
      <div className="px-4 py-3 flex items-start justify-between gap-3">
        <div className="flex items-start gap-2 min-w-0">
          <FileText className="w-4 h-4 text-gray-300 flex-shrink-0 mt-0.5" />
          <div className="min-w-0">
            <div className="text-sm font-medium text-gray-700 truncate">{dokName}</div>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              <span className="text-[10px] text-gray-400">{kategorieLabel}</span>
              <span className="text-[10px] text-gray-300">·</span>
              <span className="text-[10px] text-gray-400">{formatDate(dok.created_at)}</span>
              {ocrStatus && <OcrStatusBadge status={ocrStatus} />}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {isOcrRelevant && (ocrStatus === 'ausstehend' || ocrStatus === 'keine_felder') && (
            <button
              onClick={handleOcrTrigger}
              disabled={triggering}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold text-white transition-colors"
              style={{ backgroundColor: triggering ? '#9CA3AF' : '#C41E3A' }}
              title="OCR jetzt ausführen"
            >
              {triggering ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <Scan className="w-3 h-3" />
              )}
              OCR jetzt ausführen
            </button>
          )}
          {isOcrRelevant && ocrFelder.length > 0 && (
            <button
              onClick={() => setExpanded(e => !e)}
              className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-[11px] font-semibold text-gray-500 hover:text-gray-700 transition-colors"
            >
              {expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
              {ocrFelder.length} Felder
            </button>
          )}
        </div>
      </div>

      {/* OCR-Felder-Bereich */}
      {expanded && ocrFelder.length > 0 && (
        <div className="border-t border-gray-50 px-4 pb-3 pt-3">
          <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">
            OCR-Erkannte Felder
          </div>
          <div className="space-y-2">
            {ocrFelder.map(feld => {
              const wert = getOcrFieldValue(dok.ocr_extracted_fields, feld);
              if (!wert) return null;
              const konfidenz = getOcrFieldKonfidenz(dok.ocr_extracted_fields, feld);
              const fahrzeugFeld = getFahrzeugFeldFuerOcr(feld);
              const adopted = fahrzeugFeld
                ? isFieldAdopted(wert, fahrzeug, fahrzeugFeld)
                : false;

              return (
                <div
                  key={feld}
                  className="flex items-center justify-between gap-3 py-1.5 border-b border-gray-50 last:border-0"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">
                      {getFieldLabel(feld)}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-sm font-medium text-gray-700 font-mono truncate">{wert}</span>
                      <KonfidenzIndikator konfidenz={konfidenz} />
                    </div>
                  </div>

                  <div className="flex-shrink-0">
                    {adopted ? (
                      <span className="flex items-center gap-1 text-[10px] font-semibold text-green-600">
                        <Check className="w-3.5 h-3.5" />
                        Übernommen
                      </span>
                    ) : fahrzeugFeld ? (
                      <button
                        onClick={() => handleUebernehmen(feld, wert)}
                        disabled={adopting === feld}
                        className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold border border-gray-200 text-gray-600 hover:border-gray-400 hover:text-gray-800 transition-colors disabled:opacity-50"
                      >
                        {adopting === feld ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : null}
                        Übernehmen
                      </button>
                    ) : (
                      <span className="text-[10px] text-gray-300 italic">Kein Stammfeld</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* OCR-Fehler-Hinweis */}
      {isOcrRelevant && ocrStatus === 'fehler' && (
        <div className="border-t border-red-50 px-4 py-2.5 bg-red-50 flex items-center gap-2">
          <AlertCircle className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
          <p className="text-[11px] text-red-600">OCR-Auswertung fehlgeschlagen.</p>
          <button
            onClick={handleOcrTrigger}
            disabled={triggering}
            className="ml-auto text-[11px] font-semibold text-red-600 hover:text-red-800 underline underline-offset-2"
          >
            {triggering ? 'Läuft…' : 'Erneut versuchen'}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── DokumenteTab ─────────────────────────────────────────────────────────────

export default function DokumenteTab({ fahrzeugId, fahrzeug, onFahrzeugUpdated }: DokumenteTabProps) {
  const [dokumente, setDokumente] = useState<Dokument[]>([]);
  const [ocrRunsMap, setOcrRunsMap] = useState<Record<string, OcrRun[]>>({});
  const [loading, setLoading] = useState(true);

  const loadDokumente = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('dokumente')
      .select('id, dateiname, name, kategorie, dateipfad, ocr_extracted_fields, created_at')
      .eq('fahrzeug_id', fahrzeugId)
      .order('created_at', { ascending: false });

    if (error) {
      toast.error('Dokumente konnten nicht geladen werden');
      setLoading(false);
      return;
    }

    const docs = (data ?? []) as Dokument[];
    setDokumente(docs);

    // OCR-Runs für relevante Dokumente laden
    const relevantIds = docs
      .filter(d => isOcrRelevantKategorie(d.kategorie ?? ''))
      .map(d => d.id);

    if (relevantIds.length > 0) {
      const { data: runs } = await supabase
        .from('ocr_runs')
        .select('dokument_id, fehler, created_at')
        .in('dokument_id', relevantIds)
        .order('created_at', { ascending: false });

      const map: Record<string, OcrRun[]> = {};
      for (const run of runs ?? []) {
        const rid = (run as any).dokument_id as string;
        if (!map[rid]) map[rid] = [];
        map[rid].push({ fehler: (run as any).fehler, created_at: (run as any).created_at });
      }
      setOcrRunsMap(map);
    }

    setLoading(false);
  }, [fahrzeugId]);

  useEffect(() => { loadDokumente(); }, [loadDokumente]);

  async function handleOcrTrigger(dokumentId: string) {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    const res = await fetch('/api/ocr-trigger', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ dokument_id: dokumentId }),
    });

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      toast.error(body.error ?? 'OCR fehlgeschlagen');
    } else {
      toast.success('OCR abgeschlossen');
      await loadDokumente();
    }
  }

  async function handleUebernehmen(
    _dokumentId: string,
    fahrzeugFeld: string,
    wert: string,
  ) {
    let updateWert: string | number = wert;
    if (NUMERIC_FAHRZEUG_FELDER.has(fahrzeugFeld)) {
      const parsed = parseEuropeanNumber(wert);
      if (parsed === null) {
        toast.error(`Ungültiger Zahlenwert: ${wert}`);
        return;
      }
      updateWert = parsed;
    }

    const { error } = await supabase
      .from('fahrzeuge')
      .update({ [fahrzeugFeld]: updateWert })
      .eq('id', fahrzeugId);

    if (error) {
      toast.error(`Feld konnte nicht übernommen werden: ${error.message}`);
    } else {
      toast.success(`${fahrzeugFeld} übernommen`);
      onFahrzeugUpdated();
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-5 h-5 animate-spin text-gray-300" />
      </div>
    );
  }

  if (dokumente.length === 0) {
    return (
      <div
        data-testid="dokumente-leer"
        className="flex flex-col items-center justify-center py-16 text-center"
      >
        <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
          <FileText className="w-5 h-5 text-gray-300" />
        </div>
        <h3 className="text-sm font-semibold text-gray-500">Noch keine Dokumente</h3>
        <p className="text-xs text-gray-400 mt-1">Dokumente werden über MCP-Tool oder Kiosk hochgeladen.</p>
      </div>
    );
  }

  return (
    <div data-testid="dokumente-tab" className="space-y-3">
      <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
        Dokumente ({dokumente.length})
      </div>
      {dokumente.map(dok => (
        <DokumentKarte
          key={dok.id}
          dok={dok}
          ocrRuns={ocrRunsMap[dok.id] ?? []}
          fahrzeug={fahrzeug}
          onOcrTrigger={handleOcrTrigger}
          onUebernehmen={handleUebernehmen}
        />
      ))}
    </div>
  );
}
